import { Box, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamCost, TeamMember, TeamMemberDetail } from '../types';
import TeamFilesModal from './TeamFilesModal';
import { SqoFile } from '@/common';
import TeamCostsModal from './TeamCostsModal';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';

const isDetailedTeamMember = (teamMember: TeamMember): teamMember is TeamMemberDetail => {
  return 'costs' in teamMember && 'files' in teamMember;
};

const Team = () => {
  const { t } = useTranslation();
  const [teamFilesModal, setTeamFilesModal] = useState<{
    isOpen: boolean;
    teamId: string;
    files: SqoFile[];
  }>({
    isOpen: false,
    teamId: '',
    files: [],
  });

  const [teamCostsModal, setTeamCostsModal] = useState<{
    isOpen: boolean;
    teamId: string;
    costs: TeamCost[];
  }>({
    isOpen: false,
    teamId: '',
    costs: [],
  });
  const { auditorVisit } = useAuditorVisitDetailContext();

  const pickAuditorColor = (status: string | undefined): string | null => {
    if (!status) return null;
    if (status === 'Potrjen') return '#8bc34a';
    if (status === 'Predlagan') return '#ffc107';
    return '#d32f2f';
  };

  useEffect(() => {
    if (!teamCostsModal.isOpen) {
      return;
    }

    const teamMember = auditorVisit.teamMembers?.find((e) => e.id === teamCostsModal.teamId);

    if (!teamMember) {
      return;
    }

    if (!isDetailedTeamMember(teamMember)) {
      return;
    }

    setTeamCostsModal((previous) => ({ ...previous, costs: teamMember.costs }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditorVisit.teamMembers]);
  const styles = {
    largeIcon: {
      width: 60,
      height: 60,
    },
  };
  return (
    <>
      <TeamFilesModal
        isOpen={teamFilesModal.isOpen}
        setIsOpen={(isOpen) => setTeamFilesModal((previous) => ({ ...previous, isOpen }))}
        teamId={teamFilesModal.teamId}
        files={teamFilesModal.files}
      />
      <TeamCostsModal
        isOpen={teamCostsModal.isOpen}
        setIsOpen={(isOpen) => setTeamCostsModal((previous) => ({ ...previous, isOpen }))}
        costs={teamCostsModal.costs}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!!auditorVisit.teamMembers && (
          <List
            sx={{
              display: 'grid',
              'grid-template-columns': 'repeat(2, 1fr)',
            }}
          >
            {auditorVisit.teamMembers.map((member) => (
              <ListItem
                key={member.id}
                sx={{
                  backgroundColor: pickAuditorColor(member.status),
                  margin: '0.5rem',
                  border: '0.5px solid #bdbdbd',
                  borderRadius: '8px',
                  maxWidth: '40vw',
                }}
              >
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={member.name}
                  secondary={
                    <Box>
                      <Typography>
                        {t('Role', { defaultValue: 'Role' })}: {member.role}
                      </Typography>
                      <Typography>
                        {t('Status', { defaultValue: 'Status' })}: {member.status}
                      </Typography>
                      <Typography>
                        {t('Class Intersection', { defaultValue: 'Relevant classifications' })}:
                        {member.classIntersection}
                      </Typography>
                      <Typography>
                        {t('Email', { defaultValue: 'Email' })}: {member.email || 'N/A'}
                      </Typography>
                      <Typography>
                        {t('Phone', { defaultValue: 'Phone' })}: {member.phone || 'N/A'}
                      </Typography>
                      <Typography>
                        {t('City', { defaultValue: 'City' })}: {member.city || 'N/A'}
                      </Typography>
                    </Box>
                  }
                />

                {auditorVisit.isCurrentAuditorTeamLeader && isDetailedTeamMember(member) && (
                  <>
                    <Tooltip title={t('Costs', { defaultValue: 'Costs' })} placement="top">
                      <IconButton
                        onClick={() =>
                          setTeamCostsModal({
                            isOpen: true,
                            teamId: member.id,
                            costs: member.costs,
                          })
                        }
                      >
                        <AccountBalanceWalletIcon fontSize="large" />
                      </IconButton>
                    </Tooltip>
                    {member.costs.every((cost) => cost.isConfirmedByLeader) && (
                      <Tooltip
                        title={t('Costs are confirmed by leader', {
                          defaultValue: 'Costs are confirmed by leader',
                        })}
                        placement="top"
                      >
                        <IconButton
                          onClick={() =>
                            setTeamCostsModal({
                              isOpen: true,
                              teamId: member.id,
                              costs: member.costs,
                            })
                          }
                        >
                          <CheckCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('Files', { defaultValue: 'Files' })} placement="top">
                      <IconButton
                        onClick={() =>
                          setTeamFilesModal({
                            isOpen: true,
                            teamId: member.id,
                            files: member.files,
                          })
                        }
                      >
                        <InsertDriveFileOutlinedIcon fontSize="large" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </>
  );
};

export default Team;
